import React, { useEffect, memo, useState } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { InputValues, FilterItem, FilterSetting } from './InterfaceTypes';
import Filter, { OnValueChange } from './Filter';

interface SettingWidgetProps {
  businessType: string | null;
  filterItem: FilterItem;
  filterSettings: FilterSetting[];
  onVendorIdChange: (vendorId: number) => void;
  searchData: (value: string, data: InputValues[]) => void;
  settingsData: InputValues[];
  vendorId: number;
}

function SettingsWidget(props: Readonly<SettingWidgetProps>) {
  const filterSetting = props.filterSettings.find(
    (o) => o.value === props.filterItem.value
  );

  const initialFilterObj: InputValues = {
    filterOption: filterSetting?.filterOptions.at(0)?.value ?? '',
    operator: filterSetting?.operators.at(0)?.value ?? '',
    value:
      filterSetting?.filterOptions.at(0)?.defaultOptionValue ??
      filterSetting?.filterOptions.at(0)?.intialValue ??
      '',
  };

  const [filterValues, setFilterValues] = useState<InputValues[]>(
    props.settingsData.length === 0 ? [initialFilterObj] : props.settingsData
  );

  const onAddClick = () => {
    function getNextUnusedCombination(): InputValues | undefined {
      const usedCombinations = filterValues.map((v) =>
        [v.filterOption, v.operator].join()
      );

      for (const { value: filterOption } of filterSetting?.filterOptions ||
        []) {
        for (const { value: operator } of filterSetting?.operators || []) {
          if (!usedCombinations.includes([filterOption, operator].join())) {
            return { filterOption, operator, value: '' };
          }
        }
      }
    }
    const nextUnusedCombination = getNextUnusedCombination();
    if (nextUnusedCombination) {
      const filtervalues = [...filterValues, nextUnusedCombination];
      setFilterValues(filtervalues);
      props.searchData(props.filterItem.value, filtervalues);
    }
  };

  const onDeleteClick = (index: number) => {
    const filtervalues = filterValues.filter((f, i) => i !== index);
    setFilterValues(filtervalues);
    props.searchData(props.filterItem.value, filtervalues);
  };

  const setSelectedVendorId = (vendorId: number) => {
    props.onVendorIdChange(vendorId);
  };

  const onValueChange: OnValueChange = (index, name, value, inputType?) => {
    let obj: InputValues;
    if (inputType === 'range') {
      obj = {
        ...filterValues[index],
        value: {
          ...filterValues[index].value,
          [name]: value === 'Invalid Date' ? '' : value,
          ...(name === 'starts' && { ends: '' }),
        },
      };
    } else {
      const filterOption = filterSetting?.filterOptions.find(
        (o) => o.value === value
      );
      obj = {
        ...filterValues[index],
        ...(name === 'filterOption' && {
          operator: filterOption?.hideOperators
            ? ''
            : initialFilterObj.operator,
          value: filterOption?.defaultOptionValue ?? '',
        }),
        [name]: value,
      };
    }
    const filtervalues = [
      ...filterValues.slice(0, index),
      obj,
      ...filterValues.slice(index + 1),
    ];
    setFilterValues(filtervalues);
    props.searchData(props.filterItem.value, filtervalues);
  };
  useEffect(() => {
    if (
      filterValues.every(
        (d) =>
          filterSetting?.filterOptions.find((s) => s.value === d.filterOption)
            ?.intialValue
      )
    ) {
      props.searchData(props.filterItem.value, filterValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Accordion
      data-testid={`gds-vp-advanced-search-settings-widget-${props.filterItem.value}`}
      defaultExpanded
      sx={(theme) => ({
        '.MuiAccordionDetails-root': {
          padding: `0px ${theme.spacing(2)} ${theme.spacing(2)} !important`,
        },
        '.MuiAccordionSummary-content': {
          padding: `${theme.spacing(2)} !important`,
        },
        '.MuiAccordionSummary-expandIconWrapper': {
          paddingX: `${theme.spacing(2)} !important`,
        },
        '.MuiAccordionSummary-root': {
          height: 'fit-content',
        },
      })}
    >
      <AccordionSummary
        data-testid={`gds-vp-advanced-search-settings-widget-accordion-summary`}
        expandIcon={<KeyboardArrowUpIcon />}
      >
        <Typography flex={'auto'} fontSize={16} variant="bodycopysemibold">
          {props.filterItem.label}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {filterSetting &&
          filterValues.map((filter, index) => (
            <Box
              key={String(filter.filterOption + index)}
              sx={(theme) => ({
                marginTop: index === 0 ? 0 : theme.spacing(2),
              })}
            >
              <Filter
                data-testid={`gds-vp-advanced-search-settings-widget-item`}
                {...{
                  businessType: props.businessType,
                  filter,
                  filterOptions: filterSetting.filterOptions,
                  filterValues,
                  index,
                  inputType: filterSetting.operators.find(
                    (o) => o.value === filter.operator
                  )?.type,
                  onAddClick,
                  onDeleteClick,
                  onValueChange,
                  operators: filterSetting?.filterOptions.find((o) =>
                    filterValues.some((f) => f.filterOption === o.value)
                  )?.hideOperators
                    ? []
                    : filterSetting.operators,
                  setSelectedVendorId,
                  vendorIdValue: props.vendorId,
                }}
              />
            </Box>
          ))}
      </AccordionDetails>
    </Accordion>
  );
}

export default memo(SettingsWidget);
